import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  ControlGroup,
  InputGroup,
  Select,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  Divider,
  Icon,
  ButtonGroup,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import _ from "lodash";
import { withRouter } from "react-router-dom";
import { getStatusMeta as getStatusMetaGlobal, toTitleCase as toTitleCaseGlobal } from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

class CancelledSurgeries extends React.Component {
  state = {
    // Data
    appointments: [],
    branches: [],
    doctors: [],

    // Loading states
    isLoading: false,
    isGenerating: false,

    // Filters
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "",
    selectedDoctor: "",
    selectedBranchName: null,
    selectedDoctorName: null,

    // Report data
    reportData: [], // Original unfiltered data from API
    allAppointments: [], // All appointments data for client-side filtering
    groupedData: {},

    // AG Grid state
    gridApi: null,
    gridColumnApi: null,
    quickFilterText: "",
  };

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });

      // Load branches and doctors in parallel
      const [branchesResponse, doctorsResponse] = await Promise.all([this.loadBranches(), this.loadDoctors()]);

      // Default branch from global selection if available
      if (Array.isArray(this.state.branches) && this.state.branches.length) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();
        const match = preferredName ? this.state.branches.find((b) => norm(b.name) === norm(preferredName)) : null;
        if (match)
          this.setState({
            selectedBranch: match._id,
            selectedBranchName: match.name,
          });
      }

      // Load appointments for today
      await this.loadAppointments();
    } catch (err) {
      console.error("Error loading initial data:", err);
      cogoToast.error("Failed to load initial data");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      // Use the existing branch master API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});

      // Normalize different response structures to an array of {_id, name}
      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      console.log("🔍 DEBUG: Branches loaded:", branches.length);
      this.setState({ branches });
      return branches;
    } catch (err) {
      console.error("Error loading branches:", err);
      // Try alternative API endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`, {});
        const rawBranches = response?.data || response || [];
        const branches = (Array.isArray(rawBranches) ? rawBranches : [])
          .map((site, idx) => ({
            _id: site?._id || site?.id || String(idx),
            name: site?.site || site?.siteName || site?.name || site?.branchName || site?.label || `Site ${idx + 1}`,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ branches });
        return branches;
      } catch (fallbackErr) {
        console.error("Fallback branch API also failed:", fallbackErr);
        return [];
      }
    }
  };

  loadDoctors = async () => {
    try {
      // Use the existing doctor/consultant API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});

      // Transform consultants to doctor format
      const doctors = Array.isArray(response)
        ? response.map((consultant) => ({
            _id: consultant._id || consultant.id,
            name: consultant.name || consultant.consultantName,
            specialization: consultant.specialization || consultant.department,
            phone: consultant.phone || consultant.contactNumber,
            email: consultant.email || consultant.emailId,
            shortcode: consultant.shortcode || consultant.code,
          }))
        : [];

      console.log("🔍 DEBUG: Doctors/Consultants loaded:", doctors.length);
      this.setState({ doctors });
      return doctors;
    } catch (err) {
      console.error("Error loading doctors:", err);
      // Try alternative API endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`, {});
        const doctors = response?.data || response || [];
        this.setState({ doctors });
        return doctors;
      } catch (fallbackErr) {
        console.error("Fallback doctor API also failed:", fallbackErr);
        return [];
      }
    }
  };

  loadAppointments = async () => {
    try {
      this.setState({ isGenerating: true });

      const payload = {
        page: 1,
        sortBy: "scheduledDate",
        sortOrder: "asc",
        startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
        endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
        status: "Cancelled", // Filter for cancelled surgeries only
      };

      // Add filters if selected
      if (this.state.selectedBranch) {
        console.log("this.state,", this.state);
        payload.branchName = this.state.selectedBranch;
      }

      // Note: Doctor filtering is now handled client-side for better UX
      // Only apply server-side filtering for date and branch
      console.log("🔍 DEBUG: API filters applied:", {
        dateRange: `${payload.startDate} to ${payload.endDate}`,
        branchFilter: this.state.selectedBranch || "All branches",
        status: "Cancelled",
        doctorFilterHandledClientSide: true,
      });

      // Use the existing getSurgeries API
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`, payload);

      // Handle the API response structure properly
      const surgeries = response?.data?.items ? response.data.items : response?.data || response || [];

      console.log("🔍 DEBUG: Cancelled Surgeries API Response:", response);
      console.log("🔍 DEBUG: Cancelled surgeries loaded for report:", surgeries.length);

      // Debug: Check surgery data structure for selectedSurgeries
      if (surgeries.length > 0) {
        console.log("🔍 DEBUG: Sample surgery data structure:", surgeries[0]);
        console.log("🔍 DEBUG: Cancellation reason in first record:", surgeries[0]?.cancellationReason);
      }

      // Transform the surgery data to match appointment register format
      const transformedAppointments = this.transformSurgeriesToAppointments(surgeries);

      // Debug: Check doctor names in transformed data
      if (this.state.selectedDoctor && transformedAppointments.length > 0) {
        console.log("🔍 DEBUG: Doctor names in transformed data:", {
          selectedDoctorFilter: this.state.selectedDoctorName,
          extractedName: this.extractDoctorNameFromFormatted(this.state.selectedDoctorName),
          doctorNamesInData: [...new Set(transformedAppointments.map((apt) => apt.doctorName))],
          sampleAppointment: transformedAppointments[0],
        });
      }

      // Store all appointments for client-side filtering
      this.setState(
        {
          allAppointments: transformedAppointments,
        },
        () => {
          // Apply client-side filters after storing all data
          this.applyClientSideFilters();
        }
      );
    } catch (err) {
      console.error("Error loading appointments:", err);
      cogoToast.error("Failed to load cancelled surgeries");
      this.setState({ allAppointments: [], appointments: [] });
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  // Helper function to calculate age from date of birth
  calculateAge = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    } catch (err) {
      console.error("Error calculating age:", err);
      return null;
    }
  };

  // Helper function to create branch items for dropdown
  createBranchSelectItems = () => {
    return this.state.branches.map((branch) => branch.name).filter(Boolean);
  };

  // Helper function to create doctor items for dropdown with shortcode formatting
  createDoctorSelectItems = () => {
    return this.state.doctors
      .map((doctor) => {
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        const name = doctor.name;
        return `${shortcode}${name}`;
      })
      .filter(Boolean);
  };

  // Helper function to get formatted doctor name
  getFormattedDoctorName = (doctor) => {
    if (!doctor) return null;
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
  };

  // Helper function to extract doctor name from formatted string
  extractDoctorNameFromFormatted = (formattedName) => {
    if (!formattedName) return null;
    // Remove shortcode part (everything before " | ")
    const withoutShortcode = formattedName.includes(" | ") ? formattedName.split(" | ")[1] : formattedName;
    return withoutShortcode.trim();
  };

  // Apply client-side filtering to existing data
  applyClientSideFilters = () => {
    let filteredAppointments = [...this.state.allAppointments];

    // Apply doctor filter if selected
    if (this.state.selectedDoctor && this.state.selectedDoctorName) {
      const extractedDoctorName = this.extractDoctorNameFromFormatted(this.state.selectedDoctorName);
      console.log("🔍 DEBUG: Applying client-side doctor filter:", extractedDoctorName);

      filteredAppointments = filteredAppointments.filter((appointment) => {
        const appointmentDoctorName = appointment.doctorName || "";
        // Try multiple matching strategies
        const matches = [
          // Exact match
          appointmentDoctorName === extractedDoctorName,
          // Match without "DR. " prefix
          appointmentDoctorName.replace(/^DR\.\s*/i, "") === extractedDoctorName.replace(/^DR\.\s*/i, ""),
          // Case-insensitive match
          appointmentDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
          // Partial match (contains)
          appointmentDoctorName.toLowerCase().includes(extractedDoctorName.toLowerCase()),
        ].some(Boolean);

        return matches;
      });

      console.log("🔍 DEBUG: Client-side filtering result:", {
        original: this.state.allAppointments.length,
        filtered: filteredAppointments.length,
        extractedDoctorName,
      });
    }

    // Update state with filtered data
    this.setState({
      appointments: filteredAppointments,
      reportData: filteredAppointments,
    });

    this.groupDataByDoctor(filteredAppointments);

    // Show feedback to user
    const filterMsg = this.state.selectedDoctorName
      ? `Filtered to ${filteredAppointments.length} cancelled surgeries for ${this.extractDoctorNameFromFormatted(
          this.state.selectedDoctorName
        )}`
      : `Showing ${filteredAppointments.length} cancelled surgeries`;

    cogoToast.success(filterMsg);
  };

  // Helper function to format selected surgeries
  formatSelectedSurgeries = (selectedSurgeries) => {
    if (!selectedSurgeries || !Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
      return null;
    }

    return selectedSurgeries
      .map((surgery) => {
        let surgeryDisplay = surgery.name || "Unknown Surgery";
        return surgeryDisplay;
      })
      .join("; ");
  };

  formatSelectedGroup = (selectedSurgeries) => {
    if (!selectedSurgeries || !Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
      return null;
    }

    return selectedSurgeries.map((surgery) => surgery.group || "").join(", ");
  };

  toTitleCase = (value) => toTitleCaseGlobal(value);

  getStatusMeta = (status) => {
    const meta = getStatusMetaGlobal(status);
    return meta;
  };

  renderStatusCell = (status) => {
    if (!status) {
      return "-";
    }
    const meta = this.getStatusMeta(status);
    return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
  };
  renderIsBilledCell = (row) => getIsBilledPill(row);

  // Helper function to format IOL items with variants
  formatIOLItems = (iolItems) => {
    if (!iolItems || !Array.isArray(iolItems) || iolItems.length === 0) {
      return null;
    }

    return iolItems
      .map((item) => {
        let itemDisplay = item.itemName || "Unknown IOL";

        // Add variants if they exist
        if (item.hasVariants && item.selectedVariants && Object.keys(item.selectedVariants).length > 0) {
          const variants = [];

          // Format variants with proper names
          Object.entries(item.selectedVariants).forEach(([key, value]) => {
            if (value) {
              // Get the proper variant name from itemDetails
              let variantName = key;
              if (key === "property1" && item.itemDetails?.variantProperty1Name) {
                variantName = item.itemDetails.variantProperty1Name;
              } else if (key === "property2" && item.itemDetails?.variantProperty2Name) {
                variantName = item.itemDetails.variantProperty2Name;
              } else if (key === "property3" && item.itemDetails?.variantProperty3Name) {
                variantName = item.itemDetails.variantProperty3Name;
              }

              variants.push(`${variantName}: ${value}`);
            }
          });

          if (variants.length > 0) {
            itemDisplay += ` (${variants.join(", ")})`;
          }
        }

        return itemDisplay;
      })
      .join("; ");
  };

  // Navigate to surgery details page
  navigateToDetails = (surgery) => {
    const detailsPath = `/v3/workflow/surgery-booking/${
      surgery._id || surgery.docNo || surgery.bookingReference || surgery.surgeryId
    }`;
    window.open(detailsPath, "_blank", "noopener,noreferrer");
  };

  // AG Grid column definitions
  getColumnDefs = () => [
    {
      headerName: "Doctor/Surgeon",
      field: "doctorName",
      width: 160,
      pinned: "left",
      filter: "agTextColumnFilter",
      valueFormatter: (params) => {
        return params.value ? params.value.replace("DR. ", "") : "-";
      },
      cellStyle: { fontWeight: "600" },
    },
    {
      headerName: "Surgery ID",
      field: "docNo",
      width: 120,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.docNo || params.data.bookingId || params.data.surgeryId || "-";
      },
      cellRenderer: (params) => {
        const value = params.data.docNo || params.data.bookingId || params.data.surgeryId;
        if (!value || value === "-") return "-";

        const container = document.createElement("div");
        const link = document.createElement("span");

        link.innerHTML = value;
        link.style.color = "#137cbd";
        link.style.cursor = "pointer";
        link.style.textDecoration = "underline";
        link.style.fontWeight = "600";
        link.onclick = () => this.navigateToDetails(params.data);

        container.appendChild(link);
        return container;
      },
    },
    {
      headerName: "PID/Patient Name",
      field: "patientInfo",
      width: 200,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        const pid = params.data.patientId || params.data.pid || "-";
        const name = params.data.patientName || "-";
        return `${pid}/${name}`;
      },
      cellRenderer: (params) => {
        const pid = params.data.patientId || params.data.pid || "-";
        const name = params.data.patientName || "-";

        return `
                    <div>
                        <div style="font-weight: 600; margin-bottom: 4px;">${pid} / ${name}</div>
                    </div>
                `;
      },
    },
    {
      headerName: "Patient Phone",
      field: "patientPhone",
      width: 140,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.patientPhone || "-";
      },
      cellRenderer: (params) => {
        const phone = params.data.patientPhone || "-";
        return `
                    <div style="text-align: center;">
                        <div style="font-weight: 500;">${phone}</div>
                    </div>
                `;
      },
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Surgery Date",
      field: "surgeryDate",
      width: 140,
      filter: "agDateColumnFilter",
      valueGetter: (params) => {
        return params.data.surgeryDate ? moment(params.data.surgeryDate).format("DD/MM/YYYY") : "-";
      },
      cellRenderer: (params) => {
        const date = params.data.surgeryDate ? moment(params.data.surgeryDate).format("DD/MM/YYYY") : "-";
        const time = params.data.scheduledTime || "";

        return `
                    <div style="text-align: center;">
                        <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
                        ${time ? `<div style="font-size: 12px; color: #8A9BA8;">${time}</div>` : ""}
                    </div>
                `;
      },
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Status",
      field: "status",
      width: 130,
      filter: "agSetColumnFilter",
      valueFormatter: (params) => this.toTitleCase(params.value),
      cellRenderer: (params) => this.renderStatusCell(params.value),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Is Billed",
      field: "isBilled",
      width: 120,
      filter: "agSetColumnFilter",
      valueGetter: (params) => getIsBilledLabel(params.data),
      valueFormatter: (params) => getIsBilledLabel(params.data),
      cellRenderer: (params) => this.renderIsBilledCell(params.data),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Cancellation Reason",
      field: "cancellationReason",
      width: 250,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const reason = params.value || "-";
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 250px; color: #F5F8FA;">${reason}</div>`;
      },
    },
    {
      headerName: "Cancelled At",
      field: "cancelledAt",
      width: 160,
      filter: "agDateColumnFilter",
      valueGetter: (params) => {
        return params.data.cancelledAt ? moment(params.data.cancelledAt).format("DD/MM/YYYY HH:mm") : "-";
      },
      cellRenderer: (params) => {
        if (!params.data.cancelledAt) return "-";
        const date = moment(params.data.cancelledAt).format("DD/MM/YYYY");
        const time = moment(params.data.cancelledAt).format("HH:mm");
        return `
            <div style="text-align: center;">
                <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
                <div style="font-size: 12px; color: #8A9BA8;">${time}</div>
            </div>
        `;
      },
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Cancelled By",
      field: "cancelledByName",
      width: 150,
      filter: "agTextColumnFilter",
      cellStyle: { fontWeight: "500" },
    },
    {
      headerName: "Age",
      field: "patientAge",
      width: 80,
      filter: "agNumberColumnFilter",
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Sex",
      field: "patientGender",
      width: 80,
      filter: "agTextColumnFilter",
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Eye",
      field: "eye",
      width: 80,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const eyeColor = params.value === "RE" ? "#137CBD" : "#0F9960";
        return `<span style="background: ${eyeColor}20; color: ${eyeColor}; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 500;">${
          params.value || "-"
        }</span>`;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Diet",
      field: "diet",
      width: 120,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const isDiabetic = params.value === "DIABETIC";
        const color = isDiabetic ? "#D9822B" : "#0F9960";
        return `<span style="background: ${color}20; color: ${color}; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 500;">${
          params.value || "-"
        }</span>`;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "IOL/Implant",
      field: "lens",
      width: 250,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const isNoLens = params.value === "NO LENS";
        const style = isNoLens ? "font-style: italic; color: #8A9BA8;" : "color: #F5F8FA;";
        return `<div style="${style} line-height: 1.4; word-wrap: break-word; max-width: 250px;">${
          params.value || "NO LENS"
        }</div>`;
      },
    },
    {
      headerName: "Surgery",
      field: "surgeryName",
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const surgery = params.data.surgeryName || params.data.surgery || "-";
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 300px;">${surgery}</div>`;
      },
    },
    {
      headerName: "Surgery Group",
      field: "surgeryGroup",
      width: 200,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 200px;">${params.value}</div>`;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Anesthesia",
      field: "anesthesia",
      width: 150,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const anesthesia = params.value || "-";
        return `<div style="line-height: 1.4; text-align: center; font-weight: 500;">${anesthesia}</div>`;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Remarks",
      field: "remarks",
      width: 200,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 200px; color: #8A9BA8;">${
          params.value || "-"
        }</div>`;
      },
    },
  ];

  // Transform surgery booking data to appointment register format
  transformSurgeriesToAppointments = (surgeries) => {
    return surgeries.map((surgery, index) => {
      return {
        _id: surgery._id || surgery.id || `surgery_${index}`,
        docNo: surgery.bookingReference || surgery._id?.toString() || `BKP25U${String(index).padStart(5, "0")}`,
        surgeryId: surgery._id || surgery.id || `SX${String(index + 1).padStart(3, "0")}`,
        patientId:
          surgery.patientGenericPid ||
          surgery.patientId ||
          surgery.patientOriginalData?.sysPid ||
          `P${String(index).padStart(10, "0")}`,
        patientName: surgery.patientName || surgery.patientOriginalData?.name || "Unknown Patient",
        appointmentDate: surgery.scheduledDate ? new Date(surgery.scheduledDate) : new Date(),
        surgeryDate: surgery.scheduledDate ? new Date(surgery.scheduledDate) : new Date(),
        scheduledTime: surgery.scheduledTime || "09:00",
        patientAge:
          surgery.patientAge ||
          this.calculateAge(surgery.patientDob) ||
          this.calculateAge(surgery.patientOriginalData?.dob),
        patientGender: surgery.patientSex || surgery.patientOriginalData?.sex || "Unknown",
        patientPhone: surgery.patientPhone || surgery.patientOriginalData?.phone || "",
        patientEmail: surgery.patientEmail || surgery.patientOriginalData?.email || "",
        patientAddress: surgery.patientAddress || surgery.patientOriginalData?.address || "",
        eye: surgery.selectedSurgeries?.[0]?.laterality || "",
        diet: surgery.isDiabetic ? "DIABETIC" : "NON-DIABETIC",
        lens: this.formatIOLItems(surgery.IOLitems) || surgery.iolType || surgery.implantType || "NO LENS",
        specialNote: surgery.specialInstructions || surgery.preOpInstructions || "",
        surgeryName:
          // Use helper method to format selectedSurgeries with pricing
          this.formatSelectedSurgeries(surgery.selectedSurgeries) ||
          // Fallback to other surgery name fields
          (Array.isArray(surgery.surgeryNames) && surgery.surgeryNames.length > 0
            ? surgery.surgeryNames.join(", ")
            : typeof surgery.surgeryNames === "string" && surgery.surgeryNames.trim()
            ? surgery.surgeryNames
            : surgery.surgeryName) ||
          surgery.procedure ||
          surgery.surgeryType ||
          surgery.type ||
          "Surgery",
        surgeryGroup: this.formatSelectedGroup(surgery.selectedSurgeries) || surgery.group || surgery.payor || "",
        anesthesia: surgery.anesthesia || surgery.anaesthesiaType || surgery.anesthesiaType || "Topical",
        remarks: surgery.additionalNotes || surgery.notes || surgery.medicalNotes || "",
        patientPanel: surgery.payorPanel || surgery.patientOriginalData?.panel || "Cash",
        doctorName: surgery.doctorName || "Unknown Doctor",
        doctorId: surgery.doctorId || surgery.doctor?._id || "",
        branchName: surgery.branchName || "Unknown Branch",
        branchId: surgery.branchId || surgery.branch?._id || "",
        createdByName: surgery.createdByName || surgery.createdBy || "System",
        createdAt: surgery.createdAt || new Date(),
        bookedAt: surgery.createdAt || new Date(),
        status: surgery.status || "cancelled",
        cancellationReason: surgery.cancellationReason || "-",
        cancelledAt: surgery.cancelledAt || null,
        cancelledByName: surgery.cancelledByName || "-",
        urgencyLevel: surgery.urgencyLevel || "routine",
        anaesthesiaType: surgery.anaesthesiaType || surgery.anesthesiaType || "topical",
        estimatedDuration: surgery.estimatedDuration || 60,
        // Additional fields that might be useful
        whatsappNumber: surgery.whatsappNumber || "",
        enableWhatsappConfirmation: surgery.enableWhatsappConfirmation || false,
        enableEmailConfirmation: surgery.enableEmailConfirmation || false,
        timeFrame: surgery.timeFrame || "morning",
        slotCapacity: surgery.slotCapacity || 1,
        slotBookedCount: surgery.slotBookedCount || 1,
      };
    });
  };

  groupDataByDoctor = (data) => {
    // Always show data in a single table without grouping
    this.setState({
      groupedData: { "All Cancelled Surgeries": data },
    });
  };

  // AG Grid event handlers
  onGridReady = (params) => {
    this.setState({
      gridApi: params.api,
      gridColumnApi: params.columnApi,
    });
  };

  // Get only visible/displayed columns (respects sidebar column selection)
  getDisplayedColumns = () => {
    if (!this.state.gridColumnApi) return this.getColumnDefs();
    const allColumns = this.state.gridColumnApi.getAllColumns();
    if (!allColumns) return this.getColumnDefs();
    const visibleColumns = allColumns.filter((col) => col.isVisible());
    return visibleColumns.map((col) => {
      const colDef = col.getColDef();
      return {
        headerName: colDef.headerName,
        field: colDef.field,
        ...colDef,
      };
    });
  };

  onQuickFilterChanged = (event) => {
    this.setState({ quickFilterText: event.target.value });
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  // Handle branch selection from SingleSelectWrapper
  handleBranchFilterSelect = (branchName) => {
    const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
    this.setState({
      selectedBranch: selectedBranch ? selectedBranch._id : "",
      selectedBranchName: branchName || null,
    });
  };

  // Handle doctor selection from SingleSelectWrapper
  handleDoctorFilterSelect = (formattedDoctorName) => {
    // Find the doctor by matching the formatted name
    const selectedDoctor = this.state.doctors.find((doctor) => {
      const formatted = this.getFormattedDoctorName(doctor);
      return formatted === formattedDoctorName;
    });

    console.log("🔍 DEBUG: Doctor filter selection:", {
      formattedDoctorName,
      selectedDoctor,
      extractedName: this.extractDoctorNameFromFormatted(formattedDoctorName),
    });

    this.setState(
      {
        selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
        selectedDoctorName: formattedDoctorName || null,
      },
      () => {
        // Apply filtering immediately if we have data
        if (this.state.allAppointments.length > 0) {
          this.applyClientSideFilters();
        }
      }
    );
  };

  generateReport = async () => {
    await this.loadAppointments();
  };

  // Helper function to get sorted and filtered data from AG Grid
  getSortedAndFilteredData = () => {
    if (this.state.gridApi) {
      const sortedData = [];
      // Use forEachNodeAfterFilterAndSort to get data in the current sort order
      this.state.gridApi.forEachNodeAfterFilterAndSort((node) => {
        sortedData.push(node.data);
      });
      return sortedData;
    }
    // Fallback to state data if grid API not available
    return this.state.appointments;
  };

  exportToCSV = () => {
    // Get sorted and filtered data
    let filteredData = this.getSortedAndFilteredData();

    // Get only visible columns (respects sidebar column selection)
    let columns = this.getDisplayedColumns();

    // Combine Age and Sex columns for CSV export
    const hasAgeColumn = columns.some((col) => col.field === "patientAge");
    const hasSexColumn = columns.some((col) => col.field === "patientGender");

    if (hasAgeColumn && hasSexColumn) {
      // Remove individual age and sex columns
      columns = columns.filter((col) => col.field !== "patientAge" && col.field !== "patientGender");

      // Find the position where age column was (to insert combined column there)
      const ageColumnIndex = this.getDisplayedColumns().findIndex((col) => col.field === "patientAge");

      // Insert combined Age/Sex column at the age column position
      columns.splice(ageColumnIndex > 0 ? ageColumnIndex : 6, 0, {
        headerName: "Age / Sex",
        field: "ageSex",
      });
    }

    const selectedBranchName =
      this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";

    // Get selected doctor name if filter is applied
    const selectedDoctorName = this.state.selectedDoctor
      ? this.state.doctors.find((d) => d._id === this.state.selectedDoctor)?.name || "Unknown Doctor"
      : "All Doctors";

    // Create comprehensive header information
    const headerInfo = [
      [`CANCELLED SURGERIES REGISTER - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}`],
      [""], // Empty row for spacing
      ["Report Details:"],
      [`Branch: ${selectedBranchName}`],
      [`Doctor/Surgeon: ${selectedDoctorName}`],
      [
        `Filter Period: ${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(this.state.toDate).format(
          "DD-MMM-YYYY"
        )}`,
      ],
      [`Generated At: ${moment().format("DD-MMM-YYYY, HH:mm:ss")}`],
      [`Total Records: ${filteredData.length}`],
      [""], // Empty row for spacing
      ["Data Export:"],
      [""], // Empty row for spacing
    ];

    // Create column headers from visible columns
    const headers = columns.map((col) => col.headerName);

    // Format data rows based on visible columns
    const dataRows = filteredData.map((appointment) => {
      return columns.map((col) => {
        const field = col.field;

        // Handle special field mappings
        if (field === "doctorName") {
          return appointment.doctorName ? appointment.doctorName.replace("DR. ", "") : "-";
        }
        if (field === "docNo") {
          return appointment.docNo || appointment.bookingId || appointment.surgeryId || "-";
        }
        if (field === "patientInfo") {
          const pid = appointment.patientId || appointment.pid || "-";
          const name = appointment.patientName || "-";
          return `${pid}/${name}`;
        }
        if (field === "surgeryDate") {
          return appointment.surgeryDate
            ? `${moment(appointment.surgeryDate).format("DD/MM/YYYY")}${
                appointment.scheduledTime ? ` ${appointment.scheduledTime}` : ""
              }`
            : "-";
        }
        if (field === "status") {
          return this.toTitleCase(appointment.status);
        }
        if (field === "ageSex") {
          const age = appointment.patientAge || "-";
          const sex = appointment.patientGender || "-";
          return `${age} / ${sex}`;
        }
        if (field === "lens") {
          return appointment.lens || "NO LENS";
        }
        if (field === "surgeryName") {
          return appointment.surgeryName || appointment.surgery || "-";
        }
        if (field === "cancellationReason") {
          return appointment.cancellationReason || "-";
        }
        if (field === "cancelledAt") {
          return appointment.cancelledAt ? moment(appointment.cancelledAt).format("DD/MM/YYYY HH:mm") : "-";
        }
        if (field === "cancelledByName") {
          return appointment.cancelledByName || "-";
        }

        // Default: return field value or dash
        return appointment[field] || "-";
      });
    });

    // Combine all data
    const csvData = [...headerInfo, headers, ...dataRows];

    // Convert to CSV format
    const csvContent = csvData
      .map((row) =>
        row
          .map((field) => {
            // Escape quotes and wrap in quotes if field contains comma, quote, or newline
            const fieldStr = String(field || "");
            if (fieldStr.includes(",") || fieldStr.includes('"') || fieldStr.includes("\n")) {
              return `"${fieldStr.replace(/"/g, '""')}"`;
            }
            return fieldStr;
          })
          .join(",")
      )
      .join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    const fileName = `Cancelled_Surgeries_${moment(this.state.fromDate).format("DD-MMM-YYYY")}_to_${moment(
      this.state.toDate
    ).format("DD-MMM-YYYY")}_Generated_${moment().format("DD-MMM-YYYY_HH-mm")}.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    cogoToast.success(`CSV exported successfully: ${fileName} (with selected columns)`);
  };

  exportToPDF = () => {
    try {
      // Get sorted and filtered data from AG Grid
      let filteredData = this.getSortedAndFilteredData();

      // Get only visible columns (respects sidebar column selection)
      let columns = this.getDisplayedColumns();

      // Combine Age and Sex columns for print preview
      const hasAgeColumn = columns.some((col) => col.field === "patientAge");
      const hasSexColumn = columns.some((col) => col.field === "patientGender");

      if (hasAgeColumn && hasSexColumn) {
        // Remove individual age and sex columns
        columns = columns.filter((col) => col.field !== "patientAge" && col.field !== "patientGender");

        // Find the position where age column was (to insert combined column there)
        const ageColumnIndex = this.getDisplayedColumns().findIndex((col) => col.field === "patientAge");

        // Insert combined Age/Sex column at the age column position
        columns.splice(ageColumnIndex > 0 ? ageColumnIndex : 6, 0, {
          headerName: "Age / Sex",
          field: "ageSex",
        });
      }

      const selectedBranchName =
        this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";
      const selectedDoctorName = this.state.selectedDoctorName
        ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
        : null;

      // Create a new window for printing
      const printWindow = window.open("", "_blank");

      // Generate HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
         <title>Cancelled Surgeries Register</title>
         <style>
        @page {
         size: A4 landscape;
         margin: 8mm;
        }
        body { 
         font-family: Arial, sans-serif; 
         margin: 10px; 
         font-size: 14px;
         color: #000;
        }
        .header {
         text-align: center;
         margin-bottom: 30px;
         border-bottom: 2px solid #000;
         padding-bottom: 20px;
        }
        .header h1 {
         margin: 0;
         font-size: 20px;
         font-weight: bold;
        }
        .header p {
         margin: 5px 0;
         font-size: 16px;
        }
        table {
         width: 100%;
         border-collapse: collapse;
         margin-top: 20px;
         font-size: 12px;
        }
        th, td {
         border: 1px solid #000;
         text-align: left;
         vertical-align: top;
        }
        th { 
         padding: 16px 10px;
         text-align: center;
        }
        td { 
         padding: 8px 10px;
         line-height: 1.6; 
        }
        tbody tr { page-break-inside: avoid; }
        th {
         background-color: #f0f0f0;
         font-weight: bold;
         text-align: center;
        }
        .no-break {
         page-break-inside: avoid;
        }
        @media print {
         @page { size: A4 landscape; margin: 8mm; }
         body { margin: 5px; }
         .header { page-break-after: avoid; }
        }
         </style>
        </head>
        <body>
         <div class="header">
          <h1>CANCELLED SURGERIES REGISTER - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}</h1>
          <p>${selectedBranchName}${selectedDoctorName ? ` • ${selectedDoctorName}` : ""} - ${moment().format(
        "DD/MM/YYYY, HH:mm"
      )}</p>
          <p>Cancelled surgeries for ${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(
        this.state.toDate
      ).format("DD-MMM-YYYY")}</p>
          <p>Total Cancelled Surgeries: ${filteredData.length}</p>
         </div>
         
         <table>
          <thead>
           <tr>
            ${columns.map((col) => `<th>${col.headerName}</th>`).join("")}
           </tr>
          </thead>
          <tbody>
           ${filteredData
             .map((appointment) => {
               const cells = columns
                 .map((col) => {
                   const field = col.field;

                   // Handle special field mappings
                   if (field === "doctorName") {
                     return `<td>${appointment.doctorName ? appointment.doctorName.replace("DR. ", "") : "-"}</td>`;
                   }
                   if (field === "docNo") {
                     return `<td>${appointment.docNo || appointment.bookingId || appointment.surgeryId || "-"}</td>`;
                   }
                   if (field === "patientInfo") {
                     return `<td><strong>${appointment.patientId || appointment.pid || "-"}/<br>${
                       appointment.patientName || "-"
                     }</strong></td>`;
                   }
                   if (field === "patientPhone") {
                     return `<td>${appointment.patientPhone || "-"}</td>`;
                   }
                   if (field === "surgeryDate") {
                     return `<td>${
                       appointment.surgeryDate
                         ? `${moment(appointment.surgeryDate).format("DD/MM/YY")}${
                             appointment.scheduledTime ? `<br><small>${appointment.scheduledTime}</small>` : ""
                           }`
                         : "-"
                     }</td>`;
                   }
                   if (field === "status") {
                     return `<td>${this.toTitleCase(appointment.status)}</td>`;
                   }
                   if (field === "ageSex") {
                     const age = appointment.patientAge || "-";
                     const sex = appointment.patientGender || "-";
                     return `<td style="text-align: center;"><strong>${age} / ${sex}</strong></td>`;
                   }
                   if (field === "eye") {
                     return `<td><strong>${appointment.eye || "-"}</strong></td>`;
                   }
                   if (field === "lens") {
                     return `<td>${appointment.lens || "NO LENS"}</td>`;
                   }
                   if (field === "surgeryName") {
                     return `<td>${appointment.surgeryName || appointment.surgery || "-"}</td>`;
                   }
                   if (field === "cancellationReason") {
                     return `<td>${appointment.cancellationReason || "-"}</td>`;
                   }
                   if (field === "cancelledAt") {
                     return `<td style="text-align: center;">${
                       appointment.cancelledAt
                         ? `${moment(appointment.cancelledAt).format("DD/MM/YY")}<br><small>${moment(appointment.cancelledAt).format("HH:mm")}</small>`
                         : "-"
                     }</td>`;
                   }
                   if (field === "cancelledByName") {
                     return `<td>${appointment.cancelledByName || "-"}</td>`;
                   }
                   if (field === "remarks") {
                     return `<td style="min-width: 100px; max-width: 200px;">${appointment.remarks || "-"}</td>`;
                   }

                   // Default: return field value or dash
                   return `<td>${appointment[field] || "-"}</td>`;
                 })
                 .join("");
               return `<tr class="no-break">${cells}</tr>`;
             })
             .join("")}
          </tbody>
         </table>
        </body>
        </html>
       `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then trigger print
      printWindow.onload = () => {
        printWindow.print();
      };

      cogoToast.success("PDF print dialog opened (with selected columns)");
    } catch (err) {
      console.error("Error exporting PDF:", err);
      cogoToast.error("Failed to export PDF");
    }
  };

  renderReportHeader = () => {
    const selectedBranchName =
      this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";
    const selectedDoctorName = this.state.selectedDoctorName
      ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
      : null;

    return (
      <div
        style={{
          textAlign: "center",
          marginBottom: "30px",
          borderBottom: "2px solid var(--border-color)",
          paddingBottom: "20px",
        }}
      >
        <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
          CANCELLED SURGERIES REGISTER - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
        </H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "5px 0",
            fontSize: "16px",
          }}
        >
          {selectedBranchName}
          {selectedDoctorName ? ` • ${selectedDoctorName}` : ""} - {moment().format("DD/MM/YYYY, HH:mm")}
        </Text>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "5px 0",
            fontSize: "14px",
          }}
        >
          Cancelled surgeries for {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
          {moment(this.state.toDate).format("DD-MMM-YYYY")}
        </Text>
      </div>
    );
  };

  renderFilters = () => {
    return (
      <Card
        elevation={2}
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <Icon icon="filter" style={{ marginRight: "10px", color: "var(--text-primary)" }} />
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Report Filters</H3>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Tag intent="danger" large>
              {this.state.appointments.length} Cancelled Surgeries
            </Tag>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "nowrap",
            marginBottom: "15px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              From Date *
            </Tag>
            <DateInput
              value={this.state.fromDate}
              onChange={(date) => this.handleDateChange("fromDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="From Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              To Date *
            </Tag>
            <DateInput
              value={this.state.toDate}
              onChange={(date) => this.handleDateChange("toDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="To Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Branch
            </Tag>
            <div style={{ width: "160px" }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Doctor/Surgeon
            </Tag>
            <div style={{ width: "180px" }}>
              <SingleSelectWrapper
                items={[`All Doctors (${this.state.doctors.length})`, ...this.createDoctorSelectItems()]}
                selectedItem={this.state.selectedDoctorName || `All Doctors (${this.state.doctors.length})`}
                placeholder="Search doctors..."
                onChange={(value) => {
                  if (value === `All Doctors (${this.state.doctors.length})`) {
                    this.handleDoctorFilterSelect(null);
                  } else {
                    this.handleDoctorFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            flexWrap: "nowrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Quick Filter
            </Tag>
            <InputGroup
              placeholder="Search all columns..."
              value={this.state.quickFilterText}
              onChange={this.onQuickFilterChanged}
              leftIcon="search"
              style={{ width: "200px" }}
              small
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button intent="primary" icon="search" onClick={this.generateReport} loading={this.state.isGenerating}>
              Generate
            </Button>

            <Button
              icon="document"
              onClick={this.exportToPDF}
              disabled={this.state.appointments.length === 0}
              title="Export to PDF"
            >
              PDF
            </Button>

            <Button
              icon="th"
              onClick={this.exportToCSV}
              disabled={this.state.appointments.length === 0}
              title="Export to CSV"
            >
              CSV
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  renderAppointmentTable = (appointments, doctorName) => {
    if (!this.state.appointments || this.state.appointments.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-secondary)",
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          <Icon icon="calendar" size={48} style={{ marginBottom: "15px", opacity: 0.5 }} />
          <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No cancelled surgeries found</div>
          <div style={{ fontSize: "14px", opacity: 0.8 }}>Try adjusting your date range or filters</div>
        </div>
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "600px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={this.state.appointments}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          quickFilterText={this.state.quickFilterText}
          animateRows={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: false,
                  suppressColumnSelectAll: false,
                  suppressColumnExpandAll: false,
                },
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
                toolPanelParams: {
                  suppressExpandAll: false,
                  suppressFilterSearch: false,
                },
              },
            ],
            defaultToolPanel: null, // No panel open by default
            hiddenByDefault: false,
            position: "right",
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 80,
            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
          }}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data._id || params.data.surgeryId}
          rowHeight={60}
          headerHeight={45}
        />
      </div>
    );
  };

  renderReportContent = () => {
    if (this.state.isGenerating) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Generating Report..."
          description="Please wait while we fetch the cancelled surgeries data"
        />
      );
    }

    if (this.state.appointments.length === 0) {
      return (
        <NonIdealState
          icon="calendar"
          title="No Cancelled Surgeries Found"
          description="No cancelled surgeries found for the selected criteria. Try adjusting your filters."
        />
      );
    }

    return (
      <div>
        {this.renderReportHeader()}

        {Object.keys(this.state.groupedData).map((groupName) => (
          <div key={groupName} style={{ marginBottom: "30px" }}>
            {this.renderAppointmentTable(this.state.groupedData[groupName], groupName)}
          </div>
        ))}
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon icon="disable" style={{ marginRight: "12px", color: "var(--intent-danger)" }} />
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Cancelled Surgeries Register</H1>
          </div>
        </div>

        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderReportContent()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(CancelledSurgeries)));
